<template>
  <div class="mobile-layout">
    <!-- 모바일 헤더 -->
    <div class="mobile-header">
      <div class="header-content">
        <template v-if="hasSubpages">
          <button class="back-button" @click="goBack">
            <Icon name="mdi:arrow-left" size="24" />
          </button>
          <h1 class="page-title">{{ currentSubpageTitle }}</h1>
        </template>
        <template v-else>
          <h1 class="page-title">{{ currentTabTitle }}</h1>
          <button v-if="route.path === '/mobile/eval/evaluation-results'" class="filter-button" @click="toggleFilterMenu">
            <Icon name="mdi:filter" size="24" />
          </button>
        </template>
      </div>
    </div>

    <!-- 메인 컨텐츠 -->
    <main class="mobile-main" :class="{ 'no-padding': route.path === '/mobile/eval/evaluation-results' }">
      <slot />
    </main>

    <!-- 서브페이지 스택 (탭별로 유지, 최상단만 표시) -->
    <div :class="['subpage-stack', { hidden: !hasSubpages }]">
      <keep-alive>
        <component
          v-for="(page, idx) in currentStack"
          :key="page.id"
          :is="page.component"
          v-show="idx === currentStack.length - 1"
          v-bind="page.props || {}"
        />
      </keep-alive>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useMobileSubpageStore } from '~/composables/useMobileSubpageStore';
import { defaultNavItems } from '~/composables/mobile/useMobileNavItems';
import { useMobileTabKey } from '~/composables/mobile/useMobileTabKey';

const route = useRoute();
const router = useRouter();

// 현재 탭 키 (모바일 탭별 스택 식별자)
const { tabKey: currentTabKey } = useMobileTabKey(route);

// 서브페이지 스택
const subpageStore = useMobileSubpageStore();
const currentStack = computed(() => subpageStore.getStack(currentTabKey.value));
const hasSubpages = computed(() => currentStack.value.length > 0);
const currentSubpageTitle = computed(() => subpageStore.peekSubpage(currentTabKey.value)?.title || '');

// 현재 탭 타이틀 (서브페이지가 아닐 때)
const currentTabTitle = computed(() => {
  // MobileBottomNav의 탭 정보를 소스로 사용
  // 정확히 일치하는 경로 확인
  const exactMatch = defaultNavItems.find(item => item.path === route.path);
  if (exactMatch) {
    return exactMatch.label;
  }

  // 경로가 탭 경로로 시작하는지 확인 (서브 경로 포함)
  const pathMatch = defaultNavItems.find(item => route.path.startsWith(item.path));
  if (pathMatch) {
    return pathMatch.label;
  }

  // 기본값
  return 'Sgate';
});

// 하단 네비게이션은 React Native에서 관리하므로 제거됨
// 이의신청 서브페이지 여부 및 타이틀
const isObjectionSubpage = computed(() => route.path.startsWith('/mobile/eval/evaluation-results/objections'));
const objectionSubpageTitle = computed(() => {
  if (!isObjectionSubpage.value) return '';
  return route.path.endsWith('/submit') ? '이의신청하기' : '이의신청목록';
});

// 모바일 헤더 상태
const showBackButton = ref(false);
const showBottomNav = ref(false);

// 뒤로가기 버튼 표시 여부
const updateBackButton = () => {
  showBackButton.value = route.path !== '/mobile' && route.path !== '/mobile/home';
};

// 뒤로가기 함수 - 서브페이지 스택이 있으면 pop, 없으면 일반 뒤로가기
const goBack = () => {
  if (hasSubpages.value) {
    subpageStore.popSubpage(currentTabKey.value);
  } else {
    if (window.history.length > 1) {
      router.go(-1);
    } else {
      router.push('/mobile/home');
    }
  }
};

// 네비게이션 페이지 이동 함수
const navigateToPage = (path: string) => {
  navigateTo(path);
};

// 전역 타입 선언
declare global {
  interface Window {
    toggleFilterMenu?: () => void;
  }
}

// 필터 메뉴 토글 (내평가결과 페이지에서만)
const toggleFilterMenu = () => {
  console.log('레이아웃에서 필터 버튼 클릭됨, 현재 경로:', route.path);
  // 내평가결과 페이지에서만 필터 메뉴 토글
  if (route.path === '/mobile/eval/evaluation-results') {
    console.log('전역 함수 호출 시도');
    // 전역 함수 직접 호출
    if (typeof window.toggleFilterMenu === 'function') {
      window.toggleFilterMenu();
    } else {
      console.log('전역 함수가 등록되지 않음');
    }
  }
};

// 라우트 변경 감지
watch(
  () => route.path,
  () => {
    updateBackButton();
  },
  { immediate: true },
);

// 모바일 반응형 viewport 설정
useHead({
  meta: [
    {
      name: 'viewport',
      content: 'width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no',
    },
  ],
});
</script>

<style scoped lang="scss">
.mobile-layout {
  position: relative;
  min-height: 100vh;
  background-color: #f8fafc;
}

.mobile-header {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 1000;
  background: white;
  border-bottom: 1px solid #e2e8f0;

  .header-content {
    display: flex;
    align-items: center;
    padding: 12px 16px;
    height: 56px;
    position: relative;

    .back-button {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 40px;
      height: 40px;
      border: none;
      background: none;
      border-radius: 8px;
      cursor: pointer;
      color: #374151;
      transition: background-color 0.2s;

      &:hover {
        background-color: #f3f4f6;
      }
    }

    .filter-button {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 40px;
      height: 40px;
      border: none;
      background: none;
      border-radius: 8px;
      cursor: pointer;
      transition: background-color 0.2s;
      color: #3b82f6;
      position: absolute;
      right: 16px;

      &:hover {
        background-color: #eff6ff;
      }
    }

    .page-title {
      margin: 0;
      font-size: 20px;
      font-weight: 600;
      color: #111827;
      flex: 1;
      text-align: center;
    }
  }
}

.mobile-main {
  position: fixed;
  top: 56px; /* 헤더 바로 아래부터 시작 */
  left: 0;
  right: 0;
  bottom: 0; /* 하단 네비게이션 제거로 인한 조정 */
  padding: 16px;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  background-color: #f8fafc;

  &.no-padding {
    padding: 0;
    overflow: hidden; /* 평가결과 페이지는 내부에서 스크롤 처리 */
  }

  /* 스크롤바 표시 */
  scrollbar-width: thin; /* Firefox */
  scrollbar-color: #cbd5e1 #f1f5f9; /* Firefox */

  &::-webkit-scrollbar {
    width: 6px; /* Chrome/Safari/Opera */
  }

  &::-webkit-scrollbar-track {
    background: #f1f5f9;
    border-radius: 3px;
  }

  &::-webkit-scrollbar-thumb {
    background: #cbd5e1;
    border-radius: 3px;

    &:hover {
      background: #94a3b8;
    }
  }
}

/* 서브페이지 스택 컨테이너 - 메인 페이지 위에 완전히 덮음 */
.subpage-stack {
  position: fixed;
  top: 56px; /* 헤더 아래 */
  left: 0;
  right: 0;
  bottom: 0; /* 하단 네비게이션 제거로 인한 조정 */
  background-color: #f8fafc; /* 메인과 동일한 배경색 */
  z-index: 1050; /* 메인 컨텐츠(z-index: auto/0) 위에 표시 */
  display: flex;
  flex-direction: column;
  overflow: hidden;

  &.hidden {
    display: none;
  }

  /* 서브페이지 내부 컨텐츠 컨테이너 */
  > * {
    width: 100%;
    height: 100%;
    overflow-y: auto;
    -webkit-overflow-scrolling: touch;
    background-color: #f8fafc;
  }
}

/* 모바일 최적화 */
@media (max-width: 768px) {
  .mobile-main {
    padding: 12px;
  }

  .mobile-header .header-content {
    padding: 12px;
  }
}

/* 매우 작은 화면 */
@media (max-width: 480px) {
  .mobile-main {
    padding: 8px;
  }

  .mobile-header .header-content {
    padding: 8px 12px;
  }
}
</style>
